import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import Seo from "../seo/Seo";
import {
  organizationSchema,
  webPageSchema,
  breadcrumbSchema,
  faqSchema,
  resultListSchema,
} from "../seo/schema";

import PageHeader from "../components/PageHeader";
import Breadcrumbs from "../components/Breadcrumbs";
import MonthlyChartTable from "../components/MonthlyChartTable";
import MonthYearFilter from "../components/MonthYearFilter";
import AppPromo from "../components/AppPromo";
import LoadingNumber from "../components/LoadingNumber";

import { GAMES_BY_TIME } from "../data/games";
import { useNowIST, useChartResults } from "../lib/useResults";
import {
  fetchGameResultForLive,
  formatISTDate,
  formatMonthYear,
  pad2,
  getDateInfoIST,
} from "../lib/results";
import NotFound from "./NotFound";

function useSingleGameLive(game) {
  const [state, setState] = useState({
    today: null,
    yesterday: null,
    shouldShowToday: false,
    isLoading: true,
  });

  React.useEffect(() => {
    let cancelled = false;
    if (!game) return undefined;

    fetchGameResultForLive(game)
      .then((r) => {
        if (!cancelled) {
          setState({
            today: r.today,
            yesterday: r.yesterday,
            shouldShowToday: r.shouldShowToday,
            isLoading: false,
          });
        }
      })
      .catch((err) => {
        console.error(`Failed to load ${game.name}:`, err);
        if (!cancelled) {
          setState((s) => ({ ...s, isLoading: false }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [game]);

  return state;
}

/**
 * One page per game.
 *
 * These carry the long-tail intent — "gali satta result today", "faridabad
 * result time" — that the single-page site could never target, because one URL
 * can only ever rank for one primary keyword.
 */
const GameResult = ({ gameKey }) => {
  const game = useMemo(
    () => GAMES_BY_TIME.find((g) => g.key === gameKey) || null,
    [gameKey]
  );

  const nowIST = useNowIST();
  const currentYear = nowIST.getFullYear();
  const currentMonthIndex = nowIST.getMonth();

  const [selectedMonthIndex, setSelectedMonthIndex] =
    useState(currentMonthIndex);
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const games = useMemo(() => (game ? [game] : []), [game]);
  const { chartResults, isLoading: chartLoading } = useChartResults(
    selectedYear,
    games
  );

  const live = useSingleGameLive(game);

  // Hooks must run before this branch, so the guard sits here rather than at
  // the top of the component.
  if (!game) return <NotFound />;

  const todayStr = formatISTDate(nowIST);
  const todayISO = nowIST.toISOString();
  const path = `/${game.slug}`;

  const showToday = live.today != null && live.shouldShowToday;
  const todayValue = showToday ? pad2(live.today) : null;
  const yesterdayValue = live.yesterday != null ? pad2(live.yesterday) : null;

  const title = todayValue
    ? `${game.name} Satta Result Today ${todayValue} — ${todayStr} | Bagad Satta Company`
    : `${game.name} Satta Result Today ${todayStr} — ${game.timeLabel} Live Chart`;

  const description = todayValue
    ? `${game.name} satta result today (${todayStr}) is ${todayValue}. Declared at ${game.timeLabel} IST. See yesterday's number and the full ${game.name} record chart for ${currentYear}, 2025 and 2024.`
    : `${game.name} satta result today, ${todayStr}. ${game.name} is declared daily at ${game.timeLabel} IST — the number appears here automatically. Full ${game.name} monthly chart from 2024 onwards.`;

  const { yesterday } = getDateInfoIST(nowIST);

  // Recent declared numbers, used for the schema ItemList and the summary card.
  const recentRows = (() => {
    const record = chartResults.find((x) => x.game.key === game.key);
    const byMonth = record?.monthDataByMonth || {};
    const out = [];
    for (let i = 0; i < 12 && out.length < 10; i += 1) {
      const d = new Date(nowIST);
      d.setDate(d.getDate() - i);
      if (d.getFullYear() !== selectedYear) continue;
      const value = byMonth[d.getMonth() + 1]?.[d.getDate()];
      if (value) {
        out.push({ date: formatISTDate(d), value: pad2(value) });
      }
    }
    return out;
  })();

  const gameFaqs = [
    {
      q: `What time is the ${game.name} satta result declared?`,
      a: `${game.name} is declared every day at ${game.timeLabel} IST. This page updates on its own once that time passes, so there is no need to refresh.`,
    },
    {
      q: `What is the ${game.name} result today?`,
      a: todayValue
        ? `The ${game.name} result for ${todayStr} is ${todayValue}.`
        : `The ${game.name} result for ${todayStr} has not been declared yet. It is published at ${game.timeLabel} IST and will appear on this page automatically.`,
    },
    {
      q: `Where can I see the old ${game.name} chart?`,
      a: `Use the year and month selectors on this page to open any month from 2024 to ${currentYear}. Every declared ${game.name} number for that month is listed by date.`,
    },
    {
      q: `Was yesterday's ${game.name} result different?`,
      a: yesterdayValue
        ? `Yesterday (${formatISTDate(
            new Date(yesterday.year, yesterday.month - 1, yesterday.day)
          )}) the ${game.name} result was ${yesterdayValue}.`
        : `Yesterday's ${game.name} number is shown in the summary at the top of this page as soon as it loads.`,
    },
  ];

  const trail = [
    { name: "Home", path: "/" },
    { name: "Satta Chart", path: "/satta-chart" },
    { name: `${game.name} Result`, path },
  ];

  const otherGames = GAMES_BY_TIME.filter((g) => g.key !== game.key);

  return (
    <>
      <Seo
        path={path}
        title={title}
        description={description}
        keywords={`${game.name} satta result, ${game.name} result today, ${game.name} satta chart, ${game.name} result time, ${game.aliases.join(", ")}`}
        modifiedTime={todayISO}
        schema={[
          organizationSchema(),
          webPageSchema({ path, title, description, modifiedTime: todayISO }),
          breadcrumbSchema(trail),
          faqSchema(gameFaqs),
          resultListSchema({
            game,
            path,
            rows: recentRows,
            dateISO: todayISO,
          }),
        ]}
      />

      <div className="page-banner">
        <div className="container page-padding">
          <Breadcrumbs trail={trail} />

          <PageHeader
            h1={`${game.name} Satta Result Today`}
            intro={`${game.name} is declared daily at ${game.timeLabel} IST. Today's number, yesterday's number and the complete ${game.name} record chart.`}
            nowIST={nowIST}
            showClock
          />

          <section className="card" aria-labelledby="today-title">
            <h2 className="card-title" id="today-title">
              {game.name} Result — {todayStr}
            </h2>

            <div className="result-highlight">
              <div className="result-highlight-box">
                <span className="result-highlight-label">Yesterday</span>
                {yesterdayValue ? (
                  <strong className="result-highlight-value">
                    {yesterdayValue}
                  </strong>
                ) : live.isLoading ? (
                  <LoadingNumber />
                ) : (
                  <strong className="result-highlight-value muted">XX</strong>
                )}
              </div>

              <div className="result-highlight-box result-highlight-today">
                <span className="result-highlight-label">Today</span>
                {todayValue ? (
                  <strong className="result-highlight-value">
                    {todayValue}
                  </strong>
                ) : live.isLoading ? (
                  <LoadingNumber />
                ) : (
                  <strong className="result-highlight-value muted">XX</strong>
                )}
              </div>

              <div className="result-highlight-box">
                <span className="result-highlight-label">Result Time</span>
                <strong className="result-highlight-value result-highlight-time">
                  {game.timeLabel}
                </strong>
              </div>
            </div>

            {/*
              A direct, quotable answer right under the numbers. AI Overviews
              and featured snippets lift a single self-contained sentence far
              more readily than they lift a table.
            */}
            <p className="answer-box">
              {todayValue
                ? `The ${game.name} satta result today, ${todayStr}, is ${todayValue}. It was declared at ${game.timeLabel} IST.`
                : `The ${game.name} satta result for ${todayStr} has not been declared yet. ${game.name} is published daily at ${game.timeLabel} IST.`}
            </p>
          </section>

          <AppPromo />

          <section className="card monthly-card" aria-labelledby="game-chart-title">
            <h2 className="card-title card-h3" id="game-chart-title">
              {game.name} Chart —{" "}
              {formatMonthYear(selectedMonthIndex, selectedYear)}
            </h2>

            <MonthYearFilter
              selectedYear={selectedYear}
              selectedMonthIndex={selectedMonthIndex}
              currentYear={currentYear}
              currentMonthIndex={currentMonthIndex}
              onYearChange={(yr) => {
                setSelectedYear(yr);
                if (yr === currentYear && selectedMonthIndex > currentMonthIndex) {
                  setSelectedMonthIndex(currentMonthIndex);
                }
              }}
              onMonthChange={setSelectedMonthIndex}
              idPrefix={game.key}
            />

            <MonthlyChartTable
              games={games}
              chartResults={chartResults}
              selectedMonthIndex={selectedMonthIndex}
              selectedYear={selectedYear}
              isLoading={chartLoading}
            />
          </section>

          <section className="card text-content">
            <h2>About the {game.name} Satta Result</h2>
            <p>
              {game.name} is one of ten games tracked on Bagad Satta Company.
              Its number is declared once a day at{" "}
              <strong>{game.timeLabel} IST</strong>. Until that time passes, the
              day&apos;s cell shows <strong>XX</strong>; the moment the number is
              published it replaces the placeholder here automatically, with no
              refresh needed.
            </p>
            <p>
              Every {game.name} number we have published is kept permanently.
              The chart above covers {selectedYear}; switch the year selector to
              read 2024 and 2025 records in the same day-by-date layout.
            </p>

            <h2>{game.name} Result FAQ</h2>
            <dl className="faq-list">
              {gameFaqs.map((faq) => (
                <div className="faq-item" key={faq.q}>
                  <dt>{faq.q}</dt>
                  <dd>{faq.a}</dd>
                </div>
              ))}
            </dl>

            <h2>Other Satta Results</h2>
            <ul className="related-links">
              {otherGames.map((g) => (
                <li key={g.key}>
                  <Link to={`/${g.slug}`}>
                    {g.name} Satta Result — {g.timeLabel}
                  </Link>
                </li>
              ))}
            </ul>
            <p>
              See all ten games side by side on the{" "}
              <Link to="/satta-chart">full satta chart</Link> or the{" "}
              <Link to="/">live results page</Link>.
            </p>
          </section>
        </div>
      </div>
    </>
  );
};

export default GameResult;
